import { useCallback, useMemo } from 'react';
import { SelloutProductColor } from '../../../../../../../../services/modules/product-color-sellout/product-color-sellout.types';
import useOrderProductGridContext from '../../../contexts/OrderProductGrid.context';
import { SortedSkuBySizePositionProps } from './types/useSelloutTableData.types';

interface SelloutTableDataHookParams {
  selloutProductColor: SelloutProductColor;
}

export const useSelloutTableData = ({ selloutProductColor }: SelloutTableDataHookParams) => {
  const { grid } = useOrderProductGridContext();

  const sortedSkuBySizePosition: SortedSkuBySizePositionProps[] = useMemo(() => {
    const skusMap = new Map(selloutProductColor.skus.map((sku) => [sku.id, sku]));

    return grid.map((gridItem) => {
      const found = skusMap.get(gridItem.skuId);

      return {
        quantitySold: found?.sold ?? 0,
        availableAmount: found?.stock ?? 0,
        invoicedQuantity: found?.invoiced ?? 0,
        purchasedAmount: found?.purchased ?? 0,
        skuId: gridItem.skuId,
        sizeName: gridItem.productSizeName,
      };
    });
  }, [grid, selloutProductColor.skus]);

  const createSkuDataMapper = useCallback(
    (valueKey: keyof SortedSkuBySizePositionProps) => (sku: SortedSkuBySizePositionProps) => ({
      skuId: sku.skuId,
      value: sku[valueKey] as number,
      sizeName: sku.sizeName,
    }),
    [],
  );

  const { soldData, stockData, invoicedData, purchasedData } = useMemo(() => {
    const soldData = [];
    const stockData = [];
    const invoicedData = [];
    const purchasedData = [];

    for (const sku of sortedSkuBySizePosition) {
      soldData.push(createSkuDataMapper('quantitySold')(sku));
      stockData.push(createSkuDataMapper('availableAmount')(sku));
      invoicedData.push(createSkuDataMapper('invoicedQuantity')(sku));
      purchasedData.push(createSkuDataMapper('purchasedAmount')(sku));
    }

    return { soldData, stockData, invoicedData, purchasedData };
  }, [sortedSkuBySizePosition, createSkuDataMapper]);

  return {
    soldData,
    stockData,
    invoicedData,
    purchasedData,
    totalQuantitySold: selloutProductColor.totalSold,
    totalAvailableAmount: selloutProductColor.totalStock,
    totalInvoicedQuantity: selloutProductColor.totalInvoiced,
    totalPurchasedAmount: selloutProductColor.totalPurchased,
    sortedSkuBySizePosition,
  };
};
